import { Component, OnInit, ChangeDetectionStrategy, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, EventEmitter, Output, Input } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { MediaModel } from 'src/app/story/models/media.model';
import { takeUntil, filter } from 'rxjs/operators';

@Component({
  selector: 'app-view-audio',
  templateUrl: './view-audio.component.html',
  styleUrls: ['./view-audio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewAudioComponent implements OnInit, OnDestroy {
  // ---------- Child Elements ----------
  @ViewChild('audioElRef', { static: true}) audioElRef: ElementRef<HTMLAudioElement>;



  // ---------- Input / Output ----------
  @Input()
  set media(value: MediaModel) {
    if (!value) {
      return;
    }
    this.setMedia(value);
  }
  get media(): MediaModel {
    return this._media;
  }

  public get playing(): boolean {
    return this._playing;
  }
  public set playing(value: boolean) {
    if (value === this.playing) {
      return;
    }

    this._playing = value;
    this.playingChange.emit(this.playing);
  }
  private _playing: boolean;
  @Output()
  playingChange = new EventEmitter<boolean>();


  public get ready(): boolean {
    return this._ready;
  }
  public set ready(value: boolean) {
    if (value === this.ready) {
      return;
    }

    this._ready = value;
    this.readyChange.emit(this.ready);
  }
  private _ready: boolean;
  @Output()
  readyChange = new EventEmitter<boolean>();
  


  // ---------- Class Variables ----------
  mediaSupportCache: { [key: string]: CanPlayTypeResult; } = { };

  // audio state
  time: number;
  autoPlay: boolean;
  error: boolean;
  source = '';

  private _audioEl: HTMLAudioElement;
  private _media: MediaModel;
  private ngUnsubscribe = new Subject();



  // ---------- Lifecyle Events ----------
  constructor(private changeRef: ChangeDetectorRef) { }

  ngOnInit() {
    this.initMediaEvents();
    this.initMediaPlayback();
  }

  ngOnDestroy() {
    // cleanup audio
    this.pause();

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }



  // ---------- Class Methods ----------
  play() {
    if (this.ready) {
      this._audioEl.play();
    }
  }

  pause() {
    if (this.ready) {
      this._audioEl.pause();
    }
  }

  private reset() {
    this.pause();

    this.time = 0;
    this.playing = false;
    this.ready = false;
    this.error = false;

    this.changeRef.markForCheck();
  }

  private setMedia(media: MediaModel) {
    this._media = media;
    this.source = `/content/states/${this.media.data.fullUrl}`;
    this.initMediaPlayback();
  }
  
  private initMediaPlayback() {
    // clear existing
    this.autoPlay = this.playing;
    this.reset();
  }

  private initMediaEvents() {
    // set audio el
    this._audioEl = this.audioElRef.nativeElement;

    // set initial state
    this.reset();

    // track ready state
    fromEvent(this._audioEl, 'loadeddata')
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.ready = this._audioEl.readyState > 0;
      });   

    // track playing
    fromEvent(this._audioEl, 'play')
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.playing = true;
        this.changeRef.markForCheck();
      });

    // track pause
    fromEvent(this._audioEl, 'pause')
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.playing = false;
        this.changeRef.markForCheck();
      });

    // track time
    fromEvent(this._audioEl, 'timeupdate')
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.time = this._audioEl.currentTime;
      });

    // handle auto-play
    fromEvent(this._audioEl, 'canplay')
      .pipe(
        filter(() => !this.playing && this.autoPlay),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.play();
      });

    // handle errors
    fromEvent(this._audioEl, 'error')
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.error = true;
      });
  }
}
